import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { AppConstants } from '@core-lib/config';

export type InputUppercaseType =
	| 'uppercase'
	| 'capitalize'
	| 'withoutSpecialChars'
	| 'specialCharsWithAccentsAllowed'
	| 'withoutUnicodeSpecialChars'
	| 'withoutSymbolChars'
	| 'nomContactEcarpa'
	| 'withoutNumeric'
	| 'onlyNumeric'
	| 'blurTitleCase'
	| 'blurUpperCase'
	| 'startWith'
	| 'uppercaseNormalizedNFD'
	| 'withApostropheWithoutSymbolChars'
	| 'withoutSymbolButUnicodeChars';

@Directive({
    selector: '[inputUppercaseType]',
    standalone: false
})
export class InputUppercaseDirective {
	lastValue: string;

	@Input('inputUppercaseType') inputUppercaseType: InputUppercaseType[];
	@Input('startWith') startWith: string;
	@Input('formControl') formControl: AbstractControl;

	inputElement: HTMLInputElement;

	constructor(public ref: ElementRef) {
		this.inputElement = ref.nativeElement;
	}

	@HostListener('blur', ['$event']) onblur($event) {
		if ($event.target != null && $event.target.value != null && this.inputUppercaseType.includes('blurTitleCase')) {
			let value = $event.target.value.toLowerCase();
			let list = ['.', '-', ' '];
			list.forEach(e => {
				let pieces = value.split(e);
				for (let i = 0; i < pieces.length; i++) {
					let j = pieces[i].charAt(0).toUpperCase();
					pieces[i] = j + pieces[i].substr(1);
				}
				value = pieces.join(e);
			});

			this.formControl?.patchValue(`${this.inputElement.value}`.replace($event.target.value, value));

		} else if ($event.target != null && $event.target.value != null && this.inputUppercaseType.includes('blurUpperCase')) {
			$event.target.value = $event.target.value.toUpperCase();
		}
	}

	@HostListener('keyup', ['$event']) onKeyUp(e: KeyboardEvent): void {
		if (this.inputUppercaseType.includes('startWith')) {
			this.formControl?.patchValue(`${this.inputElement.value}`.replace(AppConstants.REGEX.filterNomEmetteur, ''));
		}
	}

	@HostListener('paste', ['$event']) onPaste(e: KeyboardEvent): void {
		setTimeout(() => {
			if (this.inputUppercaseType.includes('startWith')) {
				this.formControl?.patchValue(`${this.inputElement.value}`.replace(AppConstants.REGEX.filterNomEmetteur, ''));
			}
		}, 0); // setTimeout pour attendre que la valeur collée soit présente
	}

	@HostListener('keydown', ['$event']) onKeydown($event: KeyboardEvent): void {
		if (this.inputUppercaseType.includes('startWith') && this.startWith) {
			const startWith = this.startWith?.replace(AppConstants.REGEX.filterNomEmetteur, '')?.trim();
			const key = $event.key;
			const currentValue: string = this.inputElement.value ?? this.inputElement.textContent;
			const start = this.inputElement.selectionStart;
			const end = this.inputElement.selectionEnd;

			if (currentValue?.trim().includes(startWith)) {
				if (
					((key === 'Enter' || key === 'Delete') && start < startWith.length) ||
					(key === 'Backspace' && start <= startWith.length)
				) {
					$event.preventDefault();
					return;
				} else {
					const targetValue: string = [currentValue.slice(0, start), key, currentValue.slice(end)].join('');
					if (!targetValue.startsWith(startWith) || AppConstants.LIBELLE_CONTRAINT_EXPRESSION.includes(key)) {
						$event.preventDefault();
						return;
					}
				}
			}
		}
	}

	@HostListener('input', ['$event']) onInput($event) {
		const start = $event.target.selectionStart;
		const end = $event.target.selectionEnd;

		if (this.inputUppercaseType.includes('uppercase')) {
			$event.target.value = $event.target.value.toUpperCase();
		}

		if (this.inputUppercaseType.includes('uppercaseNormalizedNFD')) {
			$event.target.value = $event.target.value
				.toUpperCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
		}

		if (this.inputUppercaseType.includes('capitalize')) {
			$event.target.value = $event.target.value.charAt(0).toUpperCase() + $event.target.value.slice(1);
		}

		if (this.inputUppercaseType.includes('withoutSpecialChars')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.specialChars, '');
		}

		if (this.inputUppercaseType.includes('specialCharsWithAccentsAllowed')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.specialCharsWithAccentsAllowed, '');
		}

		if (this.inputUppercaseType.includes('withoutUnicodeSpecialChars')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.unicodeSpecialChars, '');
		}

		if (this.inputUppercaseType.includes('withoutSymbolChars')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.symbolChars, '');
		}

		if (this.inputUppercaseType.includes('nomContactEcarpa')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.nomContactEcarpa, '');
		}

		if (this.inputUppercaseType.includes('withoutNumeric')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.numericChars, '');
		}

		if (this.inputUppercaseType.includes('onlyNumeric')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.nonNumeric, '');
		}

		if (this.inputUppercaseType.includes('withApostropheWithoutSymbolChars')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.symbolCharsWithApostrophe, '');
		}

		if (this.inputUppercaseType.includes('withoutSymbolButUnicodeChars')) {
			$event.target.value = $event.target.value.replace(AppConstants.REGEX.letterAndApostropheFilter, '');
		}

		$event.target.setSelectionRange(start, end);
		$event.preventDefault();

		if (this.lastValue && $event.target.value.length > 0 && this.lastValue !== $event.target.value) {
			this.lastValue = this.ref.nativeElement.value = $event.target.value;
			// Propagation
			const evt = document.createEvent('HTMLEvents');
			evt.initEvent('input', false, true);
			event.target.dispatchEvent(evt);
		}
	}
}
